import type { ComponentPropsWithRef, ReactNode } from 'react'
import { Icon, type IconNameEnum } from '@/React/Shared/Component/Icon'

export interface BannerContainerProps extends Omit<
    ComponentPropsWithRef<'div'>,
    'title' | 'color'
> {
    color?: string | null
    children?: ReactNode
}

function BannerContainer({
    color,
    children,
    className = '',
    ...restProps
}: BannerContainerProps) {
    const bgClass = color ? `bg-${color}` : 'bg-red-600'

    return (
        <div
            {...restProps}
            className={`w-full min-h-14 ${bgClass} flex items-center ${className}`.trim()}
        >
            <div className="px-4 py-2 flex items-center gap-2 text-white flex-wrap">
                {children}
            </div>
        </div>
    )
}

export interface BannerIconProps extends ComponentPropsWithRef<'div'> {
    iconName: IconNameEnum | string
    iconClass?: string
}

function BannerIcon({
    iconName,
    iconClass = 'w-6 h-6',
    className = '',
    ...restProps
}: BannerIconProps) {
    return (
        <div
            {...restProps}
            className={`p-2 rounded-lg2 bg-red-700 ${className}`.trim()}
        >
            <Icon
                name={iconName as IconNameEnum}
                className={iconClass}
                aria-hidden="true"
            />
        </div>
    )
}

function BannerTitle({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={`font-bold tracking-wide ${className}`.trim()}
        >
            {children}
        </div>
    )
}

function BannerMessage({
    children,
    className = '',
    ...restProps
}: ComponentPropsWithRef<'div'>) {
    return (
        <div
            {...restProps}
            className={className}
        >
            {children}
        </div>
    )
}

export const Banner = Object.assign(BannerContainer, {
    Container: BannerContainer,
    Icon: BannerIcon,
    Title: BannerTitle,
    Message: BannerMessage,
})
